import { BackfillHistoryDialog } from './BackfillHistoryDialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { OrderLabel } from '../payments/OrderLabel';
import type {
  MetricsAgeing,
  MetricsSla,
  PaymentMetricsResponse,
  PaymentMetricsRow,
} from '../../api/types';
import { mapPaymentError } from '../../api/endpoints/payments';
import { DatePicker } from '../../components/ui/DatePicker';
import { KpiCard } from '../../components/ui/KpiCard';
import { SectionCard } from '../../components/ui/SectionCard';
import { formatMoney, parsePaise } from '../payments/allocation';
import { addCalendarDays, formatIstDateTime } from '../payments/paymentDates';
import { apiRange, duration, percentage, rangeError, thisWeek, trendPath } from './metricsFormat';
import { usePaymentBreaches, usePaymentMetrics, usePaymentTeamMetrics } from './metricsHooks';
import styles from './PaymentsMetricsTab.module.css';
const SLAS: [MetricsSla, string][] = [
  ['ADVANCE', 'Advance'],
  ['BALANCE', 'Balance'],
  ['APPROVAL', 'Approvals'],
  ['INVOICE', 'Invoice'],
  ['COLLECTION', 'Collection'],
  ['CLAIM_VERIFY', 'Claim verified'],
  ['CHARGE_REQUEST', 'Charge request decided'],
];
const AGEING: [MetricsAgeing, string][] = [
  ['NOT_DUE', 'Not due'],
  ['DAYS_1_15', '1–15'],
  ['DAYS_16_30', '16–30'],
  ['DAYS_31_60', '31–60'],
  ['DAYS_60_PLUS', '60+'],
];
const money = (value: string) =>
  formatMoney(value.startsWith('-') ? -parsePaise(value.slice(1)) : parsePaise(value));
function Kpis({ row, label }: { row: PaymentMetricsRow; label: string }) {
  const { t } = useTranslation();
  return (
    <section aria-label={label}>
      <h3>{label}</h3>
      <div className={styles.kpis}>
        {SLAS.map(([key, name]) => (
          <div key={key}>
            <KpiCard
              label={`${name} on-time`}
              value={percentage(row.slas[key]?.onTimePct ?? null)}
            />
            <p>
              {t('analytics.paymentsMetricsTab.onTimeOfDue', { onTime: row.slas[key]?.onTime ?? 0, due: row.slas[key]?.due ?? 0 })}
            </p>
            <KpiCard
              label={`${name} median`}
              value={duration(row.slas[key]?.medianMinutes ?? null)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
export function MetricsSummary({ data }: { data: PaymentMetricsResponse }) {
  const { t } = useTranslation();
  const [trendReadout, setTrendReadout] = useState<string | null>(null);
  const buckets = AGEING.map(([key]) => parsePaise(data.now.ageing[key].amount));
  const max = buckets.reduce((a, b) => (a > b ? a : b), 1n);
  return (
    <>
      <Kpis row={data.team} label={t('analytics.paymentsMetricsTab.teamSlas')} />
      <Kpis row={data.mine} label={t('analytics.paymentsMetricsTab.mySlas')} />
      <h2>{t('analytics.paymentsMetricsTab.now')}</h2>
      <div className={styles.now}>
        <SectionCard title={t('analytics.paymentsMetricsTab.receivables')}>
          <div className={styles.content}>
            <strong>
              {money(data.now.receivables.amount)} ·{' '}
              {t('analytics.paymentsMetricsTab.invoiceCount', { n: data.now.receivables.count })}
            </strong>
            <svg
              role="img"
              aria-label={t('analytics.paymentsMetricsTab.receivablesAgeing')}
              viewBox="0 0 500 190"
              className={styles.chart}
            >
              {AGEING.map(([key, label], index) => {
                const height = Number((buckets[index] * 120n) / max);
                return (
                  <g key={key}>
                    <rect
                      x={index * 100 + 30}
                      y={140 - height}
                      width="40"
                      height={height}
                      rx="4"
                      style={{ fill: ['var(--accent)', 'var(--status-amber-text)', 'var(--badge-red)', 'var(--status-red-text)', 'var(--status-red-text-2)'][index] }}
                    >
                      <title>
                        {`${label}: ${money(data.now.ageing[key].amount)}, ${data.now.ageing[key].count} invoices`}
                      </title>
                    </rect>
                    <text x={index * 100 + 50} y="165" textAnchor="middle">
                      {label}
                    </text>
                  </g>
                );
              })}
            </svg>
            {AGEING.map(([key, label]) => (
              <p key={key}>
                {label} · {money(data.now.ageing[key].amount)} ·{' '}
                {t('analytics.paymentsMetricsTab.invoiceCount', { n: data.now.ageing[key].count })}
              </p>
            ))}
          </div>
        </SectionCard>
        <div className={styles.stack}>
          <SectionCard title={t('analytics.paymentsMetricsTab.payables')}>
            <div className={styles.content}>
              <strong>
                {money(data.now.payables.amount)} ·{' '}
                {t('analytics.paymentsMetricsTab.itemCount', { n: data.now.payables.count })}
              </strong>
              <p>
                {t('analytics.paymentsMetricsTab.approvedNotPaid')} · {money(data.now.approvedNotPaid.amount)} ·{' '}
                {t('analytics.paymentsMetricsTab.requestCount', { n: data.now.approvedNotPaid.count })}
              </p>
              <p>
                {t('analytics.paymentsMetricsTab.dueNotRequested')} · {money(data.now.dueNotRequested.amount)} ·{' '}
                {t('analytics.paymentsMetricsTab.orderCount', { n: data.now.dueNotRequested.count })}
              </p>
            </div>
          </SectionCard>
          <SectionCard title={t('analytics.paymentsMetricsTab.unallocatedCredit')}>
            <div className={styles.content}>
              {data.now.unallocatedCredit.length === 0 ? (
                <p>{t('analytics.paymentsMetricsTab.noUnallocatedCredit')}</p>
              ) : (
                data.now.unallocatedCredit.map((credit) => (
                  <div key={credit.shipperUserId}>
                    <p>
                      <Link to={`/users/${credit.shipperUserId}`}>
                        {t('analytics.paymentsMetricsTab.shipperLink', { id: credit.shipperUserId })}
                      </Link>{' '}
                      · {money(credit.amount)} · {t('analytics.paymentsMetricsTab.receiptCount', { n: credit.count })}
                    </p>
                    {credit.atRiskCount > 0 && (
                      <p className={styles.risk}>
                        {t('analytics.paymentsMetricsTab.atRisk')} · {money(credit.atRiskAmount)} ·{' '}
                        {t('analytics.paymentsMetricsTab.receiptCount', { n: credit.atRiskCount })}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </SectionCard>
        </div>
      </div>
      <SectionCard title={t('analytics.paymentsMetricsTab.trendTitle')}>
        <div className={styles.content}>
          <p>{t('analytics.paymentsMetricsTab.trendLegend')}</p>
          <svg
            role="img"
            aria-label={t('analytics.paymentsMetricsTab.trendChartLabel')}
            viewBox="0 0 700 260"
            className={styles.chart}
          >
            <text x="0" y="25">
              100%
            </text>
            <text x="0" y="220">
              0%
            </text>
            <path
              data-testid="advance-trend"
              d={trendPath(data.weeklyTrend.map((week) => week.advanceOnTimePct))}
              fill="none"
              style={{ stroke: 'var(--accent)' }}
              strokeWidth="3"
            />
            <path
              d={trendPath(data.weeklyTrend.map((week) => week.collectionOnTimePct))}
              fill="none"
              style={{ stroke: 'var(--status-green-text)' }}
              strokeWidth="3"
            />
            {data.weeklyTrend.map((week, i) => (
              <g key={week.from}>
                {[week.advanceOnTimePct, week.collectionOnTimePct].map((value, lane) =>
                  value == null ? null : (
                    <circle
                      key={lane}
                      tabIndex={0}
                      role="button"
                      aria-label={`${week.from}: ${lane ? 'Collection' : 'Advance'} ${percentage(value)} · ${lane ? (week.collectionOnTime ?? 'unavailable') : (week.advanceOnTime ?? 'unavailable')}/${lane ? (week.collectionDue ?? 'unavailable') : (week.advanceDue ?? 'unavailable')}`}
                      onMouseEnter={() =>
                        setTrendReadout(
                          `${week.from}: ${lane ? 'Collection' : 'Advance'} ${percentage(value)} · ${lane ? (week.collectionOnTime ?? 'unavailable') : (week.advanceOnTime ?? 'unavailable')}/${lane ? (week.collectionDue ?? 'unavailable') : (week.advanceDue ?? 'unavailable')} on time`,
                        )
                      }
                      onFocus={() =>
                        setTrendReadout(
                          `${week.from}: ${lane ? 'Collection' : 'Advance'} ${percentage(value)} · ${lane ? (week.collectionOnTime ?? 'unavailable') : (week.advanceOnTime ?? 'unavailable')}/${lane ? (week.collectionDue ?? 'unavailable') : (week.advanceDue ?? 'unavailable')} on time`,
                        )
                      }
                      onClick={() =>
                        setTrendReadout(
                          `${week.from}: ${lane ? 'Collection' : 'Advance'} ${percentage(value)} · ${lane ? (week.collectionOnTime ?? 'unavailable') : (week.advanceOnTime ?? 'unavailable')}/${lane ? (week.collectionDue ?? 'unavailable') : (week.advanceDue ?? 'unavailable')} on time`,
                        )
                      }
                      cx={40 + (i * 620) / Math.max(1, data.weeklyTrend.length - 1)}
                      cy={220 - value * 2}
                      r="4"
                      style={{ fill: lane ? 'var(--status-green-text)' : 'var(--accent)' }}
                    >
                      <title>
                        {`${week.from}: ${lane ? 'Collection' : 'Advance'} ${percentage(value)}`}
                      </title>
                    </circle>
                  ),
                )}
                <text
                  x={40 + (i * 620) / Math.max(1, data.weeklyTrend.length - 1)}
                  y="250"
                  textAnchor="middle"
                  fontSize="10"
                >
                  {week.from.slice(5)}
                </text>
              </g>
            ))}
          </svg>
          {trendReadout && <p role="status">{trendReadout}</p>}
          <div className={styles.scroll}>
            <table className={styles.table} aria-label={t('analytics.paymentsMetricsTab.trendTableLabel')}>
              <thead>
                <tr>
                  <th>{t('analytics.paymentsMetricsTab.colWeek')}</th>
                  <th>{t('analytics.paymentsMetricsTab.colAdvanceOnTime')}</th>
                  <th>{t('analytics.paymentsMetricsTab.colAdvancePct')}</th>
                  <th>{t('analytics.paymentsMetricsTab.colCollectionOnTime')}</th>
                  <th>{t('analytics.paymentsMetricsTab.colCollectionPct')}</th>
                </tr>
              </thead>
              <tbody>
                {data.weeklyTrend.map((week) => (
                  <tr key={week.from}>
                    <th>
                      {t('analytics.paymentsMetricsTab.weekRange', { from: week.from, to: week.to })}
                    </th>
                    <td>
                      {week.advanceOnTime ?? 'Unavailable'} / {week.advanceDue ?? 'Unavailable'}
                    </td>
                    <td>
                      {week.advanceOnTimePct == null
                        ? 'No data'
                        : percentage(week.advanceOnTimePct)}
                    </td>
                    <td>
                      {week.collectionOnTime ?? 'Unavailable'} /{' '}
                      {week.collectionDue ?? 'Unavailable'}
                    </td>
                    <td>
                      {week.collectionOnTimePct == null
                        ? 'No data'
                        : percentage(week.collectionOnTimePct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </SectionCard>
      {data.people !== undefined && (
        <SectionCard title={t('analytics.paymentsMetricsTab.peopleTitle')}>
          <div
            className={styles.scroll}
            tabIndex={0}
            role="region"
            aria-label={t('analytics.paymentsMetricsTab.peopleTableLabel')}
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('analytics.paymentsMetricsTab.colPerson')}</th>
                  <th>{t('analytics.paymentsMetricsTab.colBreaches')}</th>
                  <th>{t('analytics.paymentsMetricsTab.colAdvance')}</th>
                  <th>{t('analytics.paymentsMetricsTab.colBalance')}</th>
                  <th>{t('analytics.paymentsMetricsTab.colApprovals')}</th>
                  <th>{t('analytics.paymentsMetricsTab.colInvoice')}</th>
                  <th>{t('analytics.paymentsMetricsTab.receivables')}</th>
                </tr>
              </thead>
              <tbody>
                {data.people.map((person) => (
                  <tr key={person.opsUserId}>
                    <th scope="row">{person.name}</th>
                    <td>{person.metrics.breaches}</td>
                    {(['ADVANCE', 'BALANCE', 'APPROVAL', 'INVOICE'] as const).map((key) => (
                      <td key={key}>
                        {key === 'APPROVAL' && (
                          <div>
                            {t('analytics.paymentsMetricsTab.onTimeOfDue', {
                              onTime: person.metrics.slas[key].onTime,
                              due: person.metrics.slas[key].due,
                            })}
                          </div>
                        )}
                        {percentage(person.metrics.slas[key].onTimePct)}
                      </td>
                    ))}
                    <td>
                      {money(person.receivables.amount)} · {person.receivables.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
      {data.money !== undefined && (
        <SectionCard title={t('analytics.paymentsMetricsTab.moneyTitle')}>
          <div className={styles.content}>
            <div className={styles.moneyKpis}>
              <KpiCard wrapValue label={t('analytics.paymentsMetricsTab.billedTaxable')} value={money(data.money.billedTaxable)} />
              <KpiCard wrapValue label={t('analytics.paymentsMetricsTab.partnerCost')} value={money(data.money.partnerCost)} />
              <KpiCard wrapValue label={t('analytics.paymentsMetricsTab.margin')} value={money(data.money.margin)} />
              <KpiCard label={t('analytics.paymentsMetricsTab.marginPct')} value={percentage(data.money.marginPct)} />
            </div>
          </div>
        </SectionCard>
      )}
    </>
  );
}
export function PaymentsMetricsTab() {
  const { t } = useTranslation();
  const current = thisWeek();
  const [choice, setChoice] = useState('0');
  const [range, setRange] = useState(current);
  const [mine, setMine] = useState(false),
    [page, setPage] = useState(0),
    [backfill, setBackfill] = useState(false);
  const error = rangeError(range.from, range.end);
  const params = error
    ? { from: current.from, to: addCalendarDays(current.end, 1) }
    : apiRange(range.from, range.end);
  const query = usePaymentMetrics(params, !error);
  const manager = !!query.data && query.data.people !== undefined && query.data.money !== undefined;
  const team = usePaymentTeamMetrics(params, !error && manager);
  const breaches = usePaymentBreaches(params, mine, page, !error);
  // Authorization is established by /metrics, and stale team data never exposes absent fields.
  const data =
    query.data && manager
      ? {
          ...query.data,
          people: team.data?.people ?? query.data.people,
          money: team.data?.money ?? query.data.money,
        }
      : query.data;
  const change = (next: typeof range) => {
    setRange(next);
    setPage(0);
  };
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h2>{t('analytics.paymentsMetricsTab.title')}</h2>
          <p>{t('analytics.paymentsMetricsTab.overnightNote')}</p>
          <p>
            {t('analytics.paymentsMetricsTab.approvalNote')}
          </p>
        </div>
        {manager && (
          <details>
            <summary>{t('analytics.paymentsMetricsTab.admin')}</summary>
            <button disabled={backfill} onClick={() => setBackfill(true)}>
              {t('analytics.paymentsMetricsTab.backfillHistory')}
            </button>
          </details>
        )}
      </header>
      <div className={styles.filters}>
        <label>
          {t('analytics.paymentsMetricsTab.range')}
          <select
            value={choice}
            onChange={(e) => {
              const value = e.target.value;
              setChoice(value);
              if (value !== 'custom') {
                const offset = Number(value);
                const from = addCalendarDays(current.from, -7 * offset);
                change({ from, end: offset === 0 ? current.end : addCalendarDays(from, 6) });
              }
            }}
          >
            {Array.from({ length: 8 }, (_, i) => (
              <option key={i} value={i}>
                {i === 0 ? 'This IST week' : `Week of ${addCalendarDays(current.from, -7 * i)}`}
              </option>
            ))}
            <option value="custom">{t('analytics.paymentsMetricsTab.customRange')}</option>
          </select>
        </label>
        <label>
          {t('analytics.paymentsMetricsTab.from')}
          <DatePicker
            value={range.from}
            max={range.end}
            disabled={choice !== 'custom'}
            onChange={(from) => change({ ...range, from })}
          />
        </label>
        <label>
          {t('analytics.paymentsMetricsTab.through')}
          <DatePicker
            value={range.end}
            min={range.from}
            max={rangeError(range.from, range.from) ? undefined : addCalendarDays(range.from, 91)}
            disabled={choice !== 'custom'}
            onChange={(end) => change({ ...range, end })}
          />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {!error && (
        <>
          {query.isLoading && <p role="status">{t('analytics.paymentsMetricsTab.loadingMetrics')}</p>}
          {query.isError && (
            <p role="alert">
              {mapPaymentError(query.error)}{' '}
              <button onClick={() => query.refetch()}>{t('analytics.paymentsMetricsTab.retryMetrics')}</button>
            </p>
          )}
          {team.isError && manager && (
            <p role="alert">
              {mapPaymentError(team.error)}{' '}
              <button onClick={() => team.refetch()}>{t('analytics.paymentsMetricsTab.retryTeam')}</button>
            </p>
          )}
          {data && <MetricsSummary data={data} />}
          <SectionCard
            title={t('analytics.paymentsMetricsTab.breachesTitle')}
            action={
              <label>
                <input
                  type="checkbox"
                  checked={mine}
                  onChange={(e) => {
                    setMine(e.target.checked);
                    setPage(0);
                  }}
                />
                {t('analytics.paymentsMetricsTab.mineOnly')}
              </label>
            }
          >
            <p className={styles.content}>
              {t('analytics.paymentsMetricsTab.breachesNote')}
            </p>
            {breaches.isLoading && <p role="status">{t('analytics.paymentsMetricsTab.loadingBreaches')}</p>}
            {breaches.isError && (
              <p role="alert">
                {mapPaymentError(breaches.error)}{' '}
                <button onClick={() => breaches.refetch()}>{t('analytics.paymentsMetricsTab.retryBreaches')}</button>
              </p>
            )}
            {breaches.data && (
              <>
                <div
                  className={styles.scroll}
                  tabIndex={0}
                  role="region"
                  aria-label={t('analytics.paymentsMetricsTab.breachesTableLabel')}
                >
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>{t('analytics.paymentsMetricsTab.colOrder')}</th>
                        <th>{t('analytics.paymentsMetricsTab.colSla')}</th>
                        <th>{t('analytics.paymentsMetricsTab.colDue')}</th>
                        <th>{t('analytics.paymentsMetricsTab.colDone')}</th>
                        <th>{t('analytics.paymentsMetricsTab.colOverdue')}</th>
                        <th>{t('analytics.paymentsMetricsTab.colOwner')}</th>
                        <th>{t('analytics.paymentsMetricsTab.colReason')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breaches.data.rows.map((row, i) => (
                        <tr key={`${row.shipmentId}-${row.sla}-${i}`}>
                          <td>
                            <OrderLabel shipmentId={row.shipmentId} />
                          </td>
                          <td>
                            {row.sla === 'PAYOUT_APPROVAL'
                              ? 'Payout approval'
                              : row.sla === 'CHARGE_APPROVAL'
                                ? 'Charge approval'
                                : (SLAS.find(([key]) => key === row.sla)?.[1] ??
                                  row.sla.replace(/_/g, ' '))}
                          </td>
                          <td>{t('analytics.paymentsMetricsTab.istTime', { time: formatIstDateTime(row.dueAt) })}</td>
                          <td>
                            {row.stillOpen
                              ? 'Still open'
                              : row.doneAt
                                ? `${formatIstDateTime(row.doneAt)} IST`
                                : '—'}
                          </td>
                          <td>{duration(row.overdueMinutes)}</td>
                          <td>
                            {row.ownerName?.trim() ||
                              (row.sla.endsWith('_APPROVAL') && row.stillOpen
                                ? 'Pending — team only'
                                : '—')}
                          </td>
                          <td>{row.reason === 'payee not ready' ? 'Payee not ready' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {breaches.data.rows.length === 0 && (
                  <p className={styles.content}>{t('analytics.paymentsMetricsTab.noBreaches')}</p>
                )}
                <div className={styles.filters}>
                  <button
                    disabled={page === 0 || breaches.isFetching}
                    onClick={() => setPage(page - 1)}
                  >
                    {t('analytics.paymentsMetricsTab.previous')}
                  </button>
                  <span>
                    {t('analytics.paymentsMetricsTab.pageSummary', { page: page + 1, total: breaches.data.totalCount })}
                  </span>
                  <button
                    disabled={(page + 1) * 50 >= breaches.data.totalCount || breaches.isFetching}
                    onClick={() => setPage(page + 1)}
                  >
                    {t('analytics.paymentsMetricsTab.next')}
                  </button>
                </div>
              </>
            )}
          </SectionCard>
        </>
      )}
      {backfill && manager && <BackfillHistoryDialog onClose={() => setBackfill(false)} />}
    </div>
  );
}
